// packages
import { geoIdentity } from 'd3-geo';
import { Component, h } from 'preact';

// components
import AnalysisTable from './components/AnalysisTable';
import asyncComponent from './components/AsyncComponent';
import BalancePower from './components/BalancePower';
import CandidateRow from './components/CandidateRow';
import Contest from './components/Contest';
import FramesChild from './components/FramesChild';
import HeadshotCandidateRow from './components/HeadshotCandidateRow';
import MapTypeButtons from './components/MapTypeButtons';

// internal
import { getJSONWithLastModified } from './utils/get-json';
import {
  determineContestTitle,
  determineDistrictTitle,
  lastUpdatedFormat,
  prepareBalanceData,
} from './election-utils';
import {
  RESULTS_IN_USE,
  showBubbleMap,
  visibleCandidatesCount,
} from './endpoints';
import { SENATE, SHOWCASE_COLORS } from './colors';
import { LENGTH_OF_CHECK_INTERVAL } from './constants';

import analysisData from 'Data/data.json';
import topology from 'Assets/geography/texas_urban.json';

const LayeredMap = asyncComponent(() =>
  import(/* webpackChunkName: "LayeredMap" */ './components/LayeredMap')
);

const pastPowerBalance = analysisData['past_balance_of_power'];

// anchor: the race's id on the results page
const SENATE_RACE = {
  slug: 'us-senate-class-2',
  label: 'U.S. Senate',
  anchor: 'us-senate',
};
const GOVERNOR_RACE = { slug: 'governor', label: 'Governor', anchor: 'governor' };

// Races in the map switcher, picked by the mapswitch slug
// (?mapswitch=lieutenant-governor). A blank or unknown slug gets the default.
const SWITCHER_SETS = {
  default: [
    SENATE_RACE,
    GOVERNOR_RACE,
    {
      slug: 'texas-railroad-commission-seat-2',
      label: 'Railroad Commission',
      anchor: 'railroad-commission-of-texas',
    },
  ],
  'lieutenant-governor': [
    SENATE_RACE,
    GOVERNOR_RACE,
    {
      slug: 'lieutenant-governor',
      label: 'Lieutenant Governor',
      anchor: 'lieutenant-governor',
    },
  ],
};

// Where the switcher's "All races" tab goes
const RESULTS_PAGE_URL =
  'https://apps.texastribune.org/features/2026/texas-general-election-live-results-2026/';

// Balance of power embed titles that differ from the chamber name
const BALANCE_TITLES: { [chamber: string]: string } = {
  'U.S. House': 'Texas’ delegation in the U.S. House',
};

// Each office's section id on the results page (index.html), so "View
// results" can jump straight to it
const OFFICE_ANCHORS: { [office: string]: string } = {
  'U.S. Senate': 'us-senate',
  Governor: 'governor',
  'Texas Railroad Commission': 'railroad-commission-of-texas',
  'Lieutenant Governor': 'lieutenant-governor',
  'Attorney General': 'attorney-general',
  Comptroller: 'comptroller',
  'Land Commissioner': 'land-commissioner',
  'Agriculture Commissioner': 'agriculture-commissioner',
  'U.S. House': 'us-house',
  'State Board of Education': 'state-board-of-education',
  'Texas Supreme Court': 'texas-supreme-court',
  'Texas Court of Criminal Appeals': 'texas-court-of-criminal-appeals',
  'Texas Fifteenth Court of Appeals': 'fifteenth-court-of-appeals',
  'Texas Senate': 'texas-senate',
  'Texas House': 'texas-house',
};

class MapSwitcher extends Component<any, { active: number }> {
  state = { active: 0 };

  componentDidMount() {
    this.updateResultsLink();
  }

  componentDidUpdate() {
    this.updateResultsLink();
  }

  // Point the page's "View full results" button (if it has one) at this race
  updateResultsLink() {
    const link = document.getElementById('results-link');
    const race = this.props.races[this.state.active];
    if (link && race) link.setAttribute('href', `${RESULTS_PAGE_URL}#${race.anchor}`);
  }

  render() {
    const { races, renderMap } = this.props;
    const active = races[this.state.active] ? this.state.active : 0;

    return (
      <div class="embed-switcher">
        <div class="embed-switcher__tabs" role="tablist">
          {races.map(({ label }, idx) => (
            <button
              type="button"
              role="tab"
              aria-selected={idx === active}
              class={
                idx === active
                  ? 'embed-switcher__tab embed-switcher__tab--active'
                  : 'embed-switcher__tab'
              }
              onClick={() => this.setState({ active: idx })}
            >
              {label}
            </button>
          ))}
          <a
            class="embed-switcher__tab embed-switcher__tab--link"
            href={RESULTS_PAGE_URL}
          >
            All races
            <span class="c-icon c-icon--baseline">
              <svg aria-hidden="true">
                <use xlinkHref="#long-arrow-right"></use>
              </svg>
            </span>
          </a>
        </div>
        {renderMap(races[active].contest)}
      </div>
    );
  }
}


class BalanceEmbed extends Component<any, { pass: number }> {
  state = { pass: 0 };

  componentDidMount() {
    this.setState({ pass: 1 });
  }

  componentDidUpdate() {
    if (this.state.pass === 1) this.setState({ pass: 2 });
  }

  render() {
    const { pastData, currentData } = this.props;

    return (
      <div class={`embed-balance ${pastData.className}-balance-graphic`}>
        {/* Same title style as the results table embeds */}
        <h3 class="seat__title embed-balance__title">
          {BALANCE_TITLES[pastData.chamber] || pastData.chamber}
        </h3>
        <div class="balance-of-power-container">
          {this.state.pass > 0 && (
            <BalancePower
              pastData={pastData}
              chamber={pastData.chamber}
              className={pastData.className}
              currentData={currentData}
              currentOnly
              fullWidth
            />
          )}
        </div>
      </div>
    );
  }
}

class App extends Component<any, any> {
  timer!: number;

  constructor() {
    super();

    // mapTypes: "By candidate"/"Breakdown" choice for each map, by map id
    this.state = { contests: [], asOf: null, mapTypes: {} };

    this.projection = geoIdentity().reflectY(true);
    this.fetchData = this.fetchData.bind(this);
  }

  fetchData() {
    // "Last updated" is when the results file last changed
    getJSONWithLastModified(RESULTS_IN_USE).then(
      ({ data: contests, lastModified }) => {
        const asOf = lastUpdatedFormat(lastModified || new Date());
        this.setState({ contests, asOf }, this.startTimer);
      }
    );
  }

  startTimer() {
    this.timer = window.setTimeout(this.fetchData, LENGTH_OF_CHECK_INTERVAL);
  }

  clearTimer() {
    window.clearTimeout(this.timer);
  }

  componentDidMount() {
    this.useButtonLink();
    this.fetchData();
  }

  componentDidUpdate() {
    this.updateResultsLink();
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  // Map embeds, and embeds built with ?button=1, get the gray "View results"
  // button, like the homepage embed, instead of the text link
  useButtonLink() {
    const { items, buttonLink } = this.props;
    const link = document.getElementById('results-link');
    const hasMap = items.some(
      ({ type }) => type === 'map' || type === 'mapswitch' || type === 'category'
    );
    if (!link || !(hasMap || buttonLink)) return;

    link.className =
      'c-button c-button--standard c-button--partner-embeds l-display-block';
    link.textContent = 'View results';
    link.parentElement?.classList.add('results-link--button');
  }

  // Point the page's "View results" link at the results page section for the
  // first thing this embed shows. The map switcher sets its own link.
  updateResultsLink() {
    const { items } = this.props;
    const link = document.getElementById('results-link');
    if (!link || items.some(({ type }) => type === 'mapswitch')) return;

    let office;
    for (const { type, slug } of items) {
      if (type === 'balance') {
        const pastData = pastPowerBalance.find(item => item.className === slug);
        office = pastData && pastData.chamber;
      } else {
        const contest = this.findContests(slug)[0];
        office = contest && contest.office;
      }
      if (office) break;
    }

    const anchor = office && OFFICE_ANCHORS[office];
    link.setAttribute(
      'href',
      anchor ? `${RESULTS_PAGE_URL}#${anchor}` : RESULTS_PAGE_URL
    );
  }

  // Seats with both a special and a general election (like HD-93) share an
  // office_slug. "<slug>-special" picks the special; the plain slug picks the
  // general (or the special, if that's the only race for the seat).
  findContests(slug) {
    const { contests } = this.state;
    const specialSlug = slug.replace(/-special$/, '');
    if (specialSlug !== slug) {
      return contests.filter(
        contest =>
          contest.office_slug === specialSlug &&
          contest.election_type === 'special'
      );
    }

    const matches = contests.filter(contest => contest.office_slug === slug);
    const general = matches.filter(
      contest => contest.election_type !== 'special'
    );
    return general.length ? general : matches;
  }

  // Annotations and notes for a special election use "<slug>-special"
  annotationType(contest) {
    return contest.election_type == 'special'
      ? contest.office_slug + '-special'
      : contest.office_slug;
  }

  renderTable(contest) {
    const { notes, annotations, embedType } = this.props;
    const { asOf } = this.state;

    return (
      <Contest
        key={contest.id}
        contest={contest}
        columnCount={CandidateRow.columnCount}
        contestTitle={determineDistrictTitle(contest)}
        embedType={embedType}
        partyName={contest.party}
        tableHeader={HeadshotCandidateRow.Header}
        asOf={asOf}
        annotations={annotations.filter(
          item => item.type === this.annotationType(contest)
        )}
        notes={notes.filter(item => item.type === this.annotationType(contest))}
        source={contest.source}
        renderCandidate={(
          candidate,
          idx,
          viewAllCandidates,
          { totalVotes }
        ) => (
          <CandidateRow
            key={candidate.id}
            ballotName={candidate.name}
            candidateId={candidate.id}
            contestIsCalled={contest.is_called}
            contestResult={contest.contest_result}
            isIncumbent={candidate.is_incumbent}
            isWinner={candidate.is_winner}
            isViewable={viewAllCandidates || idx < visibleCandidatesCount}
            party={candidate.party}
            totalVotesInContest={totalVotes}
            votes={candidate.votes}
          />
        )}
      />
    );
  }

  // Map colors for each candidate in a showcase race
  candidatesRenderFor(contest) {
    return (SHOWCASE_COLORS[contest.office] || SENATE).map(object => {
      const match = contest.candidacies.find(c => c.name == object.name);
      return { ...object, id: match && match.id };
    });
  }

  // Results table that sits above the map
  renderMapTable(contest, showLastUpdated) {
    const { notes, annotations, embedType } = this.props;
    const { asOf } = this.state;
    const candidatesRender = this.candidatesRenderFor(contest);

    return (
      <Contest
        contest={contest}
        contestTitle={determineContestTitle(contest)}
        isShowcaseRace
        candidatesRender={candidatesRender}
        annotations={annotations.filter(
          item => item.type === this.annotationType(contest)
        )}
        notes={notes.filter(item => item.type === this.annotationType(contest))}
        asOf={asOf}
        embedType={showLastUpdated ? embedType : undefined}
        source={contest.source}
        tableHeader={HeadshotCandidateRow.Header}
        columnCount={HeadshotCandidateRow.columnCount}
        renderCandidate={(
          candidate,
          idx,
          candidatesRender,
          viewAllCandidates,
          { totalVotes }
        ) => (
          <HeadshotCandidateRow
            key={candidate.id}
            ballotName={candidate.name}
            candidateId={candidate.id}
            contestIsCalled={contest.is_called}
            contestResult={contest.contest_result}
            isIncumbent={candidate.is_incumbent}
            isWinner={candidate.is_winner}
            isViewable={viewAllCandidates || idx < visibleCandidatesCount}
            party={candidate.party}
            candidateColors={candidatesRender}
            totalVotesInContest={totalVotes}
            votes={candidate.votes}
          />
        )}
      />
    );
  }

  // showLastUpdated: show the "Last updated" time under the table (the
  // three-race switcher does; single map embeds don't)
  // sizerContests: other races whose tables are drawn invisibly in the same
  // spot, so the table area is as tall as the tallest of them and the map
  // doesn't jump when switching races
  renderMap(contest, showLastUpdated = false, sizerContests = []) {
    const candidatesRender = this.candidatesRenderFor(contest);

    const mapId = `map-${contest.office_slug}`;
    const { mapTypes } = this.state;
    const typeMap = mapTypes[mapId] || 'categorical';

    return (
      <div class="embed-map" key={mapId}>
        <div class="embed-map__header">
          {/* Other races' titles sit invisibly in the same spot, so a title
              that wraps to two lines reserves that room on every race */}
          <h3 class="embed-map__title">
            <span>{contest.office}</span>
            {sizerContests
              .filter(other => other.id !== contest.id)
              .map(other => (
                <span
                  key={`title-sizer-${other.id}`}
                  class="embed-map__title-sizer"
                  aria-hidden="true"
                >
                  {other.office}
                </span>
              ))}
          </h3>
        </div>
        {/* Results table first, then the map type toggle and the map */}
        <div class="embed-map__table">
          <div key={`table-${contest.id}`}>
            {this.renderMapTable(contest, showLastUpdated)}
          </div>
          {sizerContests
            .filter(other => other.id !== contest.id)
            .map(other => (
              <div
                key={`sizer-${other.id}`}
                class="embed-map__table-sizer"
                aria-hidden="true"
              >
                {this.renderMapTable(other, showLastUpdated)}
              </div>
            ))}
        </div>
        {/* Map type toggle sits right above the map it switches */}
        {showBubbleMap && (
          <div class="embed-map__controls">
            <MapTypeButtons
              active={typeMap}
              onSelect={type =>
                this.setState({ mapTypes: { ...mapTypes, [mapId]: type } })
              }
            />
          </div>
        )}
        <div id={mapId} class="embed-map__map">
          <LayeredMap
            typeMap={typeMap}
            topology={topology}
            projection={this.projection}
            candidates={contest.candidacies}
            candidatesRender={candidatesRender}
            source={contest.source}
            counties={contest.counties}
            typeRace={mapId}
            precinctsReporting={contest.precincts_reporting}
            totalVotes={contest.total_votes}
            minLegendRows={sizerContests.reduce(
              (most, other) => Math.max(most, other.candidacies.length),
              0
            )}
          />
        </div>
      </div>
    );
  }

  // Regional breakdown grids (tiny maps + bar charts) from the results page
  renderCategories(contest) {
    if (!contest.counties || !contest.counties.length) return null;

    // Same candidates the results page charts: just the Republican and the
    // Democrat (the showcase colors also include third parties for the map)
    return (
      <div class="embed-categories" key={`category-${contest.office_slug}`}>
        <h3 class="embed-map__title">{contest.office}</h3>
        <AnalysisTable
          contest={contest}
          candidates={contest.candidacies.filter(candidate =>
            ['Republican', 'Democrat'].includes(candidate.party)
          )}
          counties={contest.counties}
          linkBase={RESULTS_PAGE_URL}
        />
      </div>
    );
  }

  renderSwitcher(slug) {
    const races = (SWITCHER_SETS[slug] || SWITCHER_SETS.default).map(race => ({
      ...race,
      contest: this.findContests(race.slug)[0],
    })).filter(race => race.contest);

    if (!races.length) return null;

    // Size the table to the tallest race in any switcher (Lieutenant Governor
    // has an extra candidate), so every switcher embed is the same height
    const sizerSlugs: string[] = [];
    Object.keys(SWITCHER_SETS).forEach(key =>
      SWITCHER_SETS[key].forEach(race => {
        if (sizerSlugs.indexOf(race.slug) === -1) sizerSlugs.push(race.slug);
      })
    );
    const sizerContests = sizerSlugs
      .map(raceSlug => this.findContests(raceSlug)[0])
      .filter(Boolean);

    return (
      <MapSwitcher
        key="mapswitch"
        races={races}
        renderMap={contest => this.renderMap(contest, true, sizerContests)}
      />
    );
  }

  renderBalance(className) {
    const pastData = pastPowerBalance.find(
      item => item.className === className
    );
    if (!pastData) return null;

    const chamberContests = this.state.contests.filter(
      contest => contest.office === pastData.chamber
    );

    return (
      <BalanceEmbed
        key={`balance-${className}`}
        pastData={pastData}
        currentData={prepareBalanceData(
          chamberContests,
          pastData.className,
          pastData.chamber,
          pastData.total_current,
          pastData.halfway
        )}
      />
    );
  }

  render() {
    const { items } = this.props;
    const { contests } = this.state;

    if (!contests.length) return <FramesChild class="embed" polling={500} />;

    return (
      <FramesChild class="embed" polling={500}>
        <div class="seat__race_wrapper">
          {items.map(({ type, slug }) => {
            if (type === 'balance') return this.renderBalance(slug);
            if (type === 'mapswitch') return this.renderSwitcher(slug);

            
            const matches = this.findContests(slug);
            if (!matches.length) return null;

            if (type === 'category') return this.renderCategories(matches[0]);

            return type === 'map'
              ? this.renderMap(matches[0])
              : matches.map(contest => this.renderTable(contest));
          })}
        </div>
      </FramesChild>
    );
  }
}

export default App;
